import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { money } from "../lib/format";

const SETTLED = ["confirmed", "paid_out"];
const FAILED = ["failed", "cancelled", "charged_back", "customer_approval_denied"];

export default function GoCardlessReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const brq = params.get("billing_request_id");
  const instant = params.get("instant");
  const mock = params.get("mock") === "1";
  const club = params.get("club") === "1";
  const gift = params.get("gift");
  const [message, setMessage] = useState(instant ? "Confirming your instant bank payment with GoCardless…" : gift ? "Confirming your Direct Debit and setting up the gift payment plan…" : "Confirming your Direct Debit mandate with GoCardless…");
  void club;

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;
    const refresh = () => ["mandate", "wallet", "payments", "ledger", "club-gifts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    const go = (msg: string, to: string) => { refresh(); setMessage(msg); setTimeout(() => navigate(to), 1200); };

    const pollGift = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/club/gifts/${gift}/status`);
        if (data.gift.status !== "pending_mandate") return go(`Direct Debit confirmed. Your gift for ${data.gift.recipient_name} is set up (code ${data.gift.code}) — the first case is being collected and we'll be in touch about dates.`, "/gift-club");
      } catch (e) { console.warn("GoCardless return poll failed, retrying", e); }
      if (attempts >= 20) { setMessage("Still waiting for GoCardless. Your gift plan will start automatically the moment the mandate is confirmed."); return; }
      setTimeout(pollGift, 2000);
    };

    const pollMandate = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(club ? "/club/mandate" : "/mandates/current");
        if (data.status === "active") {
          if (club) {
            await api.post("/club/funding", { funding: "direct_debit", mandate: "club" }).catch(() => undefined);
            qc.invalidateQueries({ queryKey: ["club"] });
            return go("Club Direct Debit active. Your quarterly case is now collected separately from your cellar.", "/club");
          }
          const pending = localStorage.getItem("pending-cellar-plan");
          if (pending) {
            localStorage.removeItem("pending-cellar-plan");
            try {
              const plan = JSON.parse(pending);
              await api.post("/subscriptions", plan);
              qc.invalidateQueries({ queryKey: ["subscriptions"] });
              return go(`Bank connected and your Cellar Plan is set up — ${money(plan.amount_pence)} ${plan.interval_unit === "weekly" ? "every week" : plan.interval === 1 ? "every month" : `every ${plan.interval} months`}. Taking you to your cellar…`, "/?tab=plan");
            } catch (e) { console.warn("pending cellar plan failed", e); return go("Mandate active — we couldn't start the plan automatically, please set the amount on your Overview.", "/?tab=plan"); }
          }
          return go("Mandate active. Taking you to your cellar…", "/cellar");
        }
      } catch (e) { console.warn("GoCardless return poll failed, retrying", e); }
      if (attempts >= 20) { setMessage("Still waiting for GoCardless. We'll credit you automatically once the mandate is confirmed — check back in a moment."); return; }
      setTimeout(pollMandate, 2000);
    };

    const pollInstant = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/instant/${instant}`);
        const p = data.payment;
        if (SETTLED.includes(p.status)) return go(`${money(p.amount_pence)} added to your cellar. Balance ${money(data.wallet.balance_pence)}.`, "/cellar?tab=ledger");
        if (FAILED.includes(p.status)) return go("The bank payment was not completed. Nothing has been charged.", "/cellar");
        if (p.gc_payment_id && attempts > 6) return go("Your bank has approved the payment. Your cellar will be credited the moment GoCardless confirms it — usually within a few minutes.", "/cellar");
      } catch (e) { console.warn("GoCardless return poll failed, retrying", e); }
      if (attempts >= 20) { setMessage("Still waiting for your bank. Your cellar will be credited automatically once the payment is confirmed."); return; }
      setTimeout(pollInstant, 2000);
    };

    (async () => {
      if (mock && brq) await api.post("/mandates/mock-complete", { billing_request_id: brq }).catch(() => undefined);
      if (mock && instant) await api.post("/instant/mock-complete", { billing_request_id: instant }).catch(() => undefined);
      instant ? pollInstant() : gift ? pollGift() : pollMandate();
    })();
    return () => { cancelled = true; };
  }, [brq, instant, mock, club, gift, navigate, qc]);

  return (
    <div className="mx-auto max-w-lg py-24 text-center" data-testid="gocardless-return-page">
      <div className="eyebrow">GoCardless</div>
      <h1 className="mt-4 font-serif text-4xl font-medium">One moment</h1>
      <p className="mt-4 text-sm text-neutral-600" data-testid="gocardless-return-message">{message}</p>
      <div className="mx-auto mt-10 h-px w-40 animate-pulse bg-[var(--ink)]" />
    </div>
  );
}
